import './menu.css';

import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { Modals, OrderType, Pages, MenuLocationType, DataLayerPromotionLocation, MealDealLocationUiOption } from '../../../types/enums';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { IMenu, IMealDealData, IMenuItemsCategoriesSubcategory, IMenuItemsCategory } from './menu.interface';
import { Data } from '../../../models/data';
import { MenuItemCard } from '../../shared/MenuItemCard/menu-item-card';
import { MealDealItemCard } from '../../shared/MealDealItemCard/meal-deal-item-card';
import { TabBar } from '../../shared/TabBar/tab-bar';
import { ITabBarData } from '../../shared/TabBar/tab-bar.interface';
import { IDataCoupon, IDataItem } from '../../../models/data.interface';
import { Page } from '../page';
import { ICustomizeItemProps } from '../../modals/CustomizeItem/customize-item.interface'
import { DataLayerService } from '../../../services/data-layer-service';
import { RecentOrders } from '../../shared/RecentOrder/recent-orders';
import { ProfileService } from '../../../services/profile-service';
import { OrderService } from '../../../services/order-service';
import { DialogCreators } from '../../../utils/dialog-creators';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { IPillBarData } from '../../shared/PillBar/pill-bar.interface';
import { PillBar } from '../../shared/PillBar/pill-bar';
import { ArrowScroller } from '../../shared/ArrowScroller/arrow-scroller';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { ICheckCouponHasQualifyingItemsParams } from '@ts/models/coupons.interface';

export class Menu extends Page {
    private readonly _idRecentOrders: string = "div_menu_items_recently_ordered";
    private readonly _profileService: ProfileService;
    private readonly _orderService: OrderService;
    private readonly _dataLayer: DataLayerService;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _item: IMenu;
    private readonly _recentOrders: RecentOrders;

    private _dataLayerObserverSet: boolean = false;

    private readonly DEBOUNCE_INTERVAL = 5;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, orderService: OrderService, dataLayer: DataLayerService, loyaltyProvider: ILoyaltyProvider) {
        super("page_menu", eventBus, data, context, localizationService);
        this._profileService = profileService;
        this._orderService = orderService;
        this._dataLayer = dataLayer;
        this._loyaltyProvider = loyaltyProvider;

        this._recentOrders = new RecentOrders(this._eventBus, this._data, this._context, this._localizationService, this._profileService, this._orderService, this._dataLayer);

        this._item = this._initialModel();
        this._eventBusListeners();
    }

    private readonly _initialModel = (): IMenu => {
        return {
            storeKey: "",
            orderTypeId: this._context.getProperty("defaultOrderType"),
            categoryId: -1,
            isScrolling: false,
            isPendingDLEvent: false
        };
    }

    public init(): void {
        super.init();
        this._showPlaceholders();
        this._renderPlaceholders();
    }

    public override show(): void {
        super.show();

        const menuRedirect = !this._data.storeKey;
        if (menuRedirect) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS);
            return;
        }

        (document.getElementById("ul_menu_items_category")?.children?.item(0) as HTMLElement)?.click();
        document.getElementById("btn_menu_menu_tab_-1")?.click();

        if (this._data.getProperty("StoreInfo").storeKey)
            this._applyCouponFromContextAsync();

        if (this._dataLayer) {
            if (this._data.getProperty("StoreInfo").storeCode === 0) {  // data has not been retrieved yet
                this._item.isPendingDLEvent = true;
            } else {
                this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.getProperty("StoreInfo").storeCode.toString(), this._profileService.user.user);
            }
        }
    }

    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, async (orderTypeId: OrderType) => {
            const isChanged = this._orderTypeChanged(orderTypeId);
            if (isChanged)
                await this._renderPageAsync();
        });
        this._eventBus.on(EventTypes.DATA_RETRIEVED, this._dataRetrievedAsync);
        this._eventBus.on(EventTypes.LOGGED_OUT, () => this._setRecentOrderedButtonState(false));
        this._eventBus.on(EventTypes.LOGGED_IN, () => this._setRecentOrderedButtonState(true));
        this._eventBus.on(EventTypes.MENU_ANCHOR, this._categoryButtonOnClick);
        this._eventBus.on(EventTypes.ORDER_COMPLETED, async () => {
            this._profileService.user.setProperty("RecentOrders", null);
            await this._recentOrders.buildRecentlyOrderedItemsSection(this._idRecentOrders);
        });
        this._eventBus.on(EventTypes.MENU_PLACEHOLDERS, this._showPlaceholders);
        this._eventBus.on(EventTypes.DATALAYER_LOADED, () => {
            this._setupDataLayerObservers();
        });
        this._eventBus.on(EventTypes.LOCATION_CHANGE, (page: Pages) => {
            if (page !== Pages.MENU) {
                this._context.setProperty("addMoreItemsClicked", false);
            }
        });
    }

    /**
     * This method sets up observers for menu items and meal deals, allowing the Data Layer to track a user viewing these elements.
     */
    private readonly _setupDataLayerObservers = (): void => {
        if (this._dataLayer && !this._dataLayerObserverSet) {
            const menuItemsParentDiv = document.getElementById("div_menu_items_main") as HTMLElement;
            if (menuItemsParentDiv) {
                this._dataLayer.setupItemsObserver(menuItemsParentDiv, '[name="div_menu_items_item"]', 'menu', {
                    datasetKey: 'id',
                    handler: this._dataLayer.view_item_list
                });
                this._dataLayerObserverSet = true;
            }

            const mealDealCategories = this._createMealDealsArrayObject();
            for (const mealDealCategory of mealDealCategories) {
                const mealDealParentDiv = document.getElementById(`div_menu_items_${mealDealCategory.id}`) as HTMLElement;
                if (mealDealParentDiv) {
                    this._dataLayer.setupItemsObserver(mealDealParentDiv, ':scope > [name="div_menu_item_hot_deal"]', DataLayerPromotionLocation.MEAL_DEAL, {
                        datasetKey: 'cid',
                        handler: this._dataLayer.view_promotion
                    });
                    this._dataLayerObserverSet = true;
                }
            }
        }
    }

    private readonly _orderTypeChanged = (orderTypeId: OrderType): boolean => {
        if (this._item.orderTypeId === orderTypeId)
            return false;

        this._item.orderTypeId = orderTypeId;
        return true;
    }

    public readonly initAsync = async (): Promise<void> => {
        await this._renderPageAsync();
        this._setEventListeners();
    }

    private readonly _dataRetrievedAsync = async (): Promise<void> => {
        if (!this._checkDataInfo())
            return;

        await this._renderPageAsync();
        this._setEventListeners();
        this._setRecentOrderedButtonState(this._profileService.user.isLoggedIn());
        await this._applyCouponFromContextAsync();

        const anchorCategoryId = this._context.getProperty("anchorCategoryId");
        if (anchorCategoryId)
            document.getElementById(`btn_menu_menu_tab_${anchorCategoryId}`)?.click();

        const hotDealId = this._context.getProperty("hotDealId");
        if (hotDealId)
            document.getElementById(`btn_menu_menu_tab_${hotDealId}`)?.click();

        this._setupDataLayerObservers();
        if (this._item.isPendingDLEvent) {
            this._item.isPendingDLEvent = false;
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.getProperty("StoreInfo").storeCode.toString(), this._profileService.user.user);
        }
    }

    private readonly _checkDataInfo = (): boolean => {
        if (this._data.getProperty("OrderTypeSubTypes").length === 0) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names(
                    "Error_ConfigurationGenericError"
                ),
                true
            );
            return false;
        }

        if (this._data.getProperty("PreModifiers").length === 0) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names(
                    "Error_ConfigurationGenericError"
                ),
                true
            );
            return false;
        }

        return true;
    }

    private async _applyCouponFromContextAsync(): Promise<void> {
        const couponId = this._context.getProperty("couponId");
        if (!couponId)
            return;

        if (this._data.getProperty("Coupons").find(x => x.CouponId === couponId)) {
            const params: ICheckCouponHasQualifyingItemsParams = {
                couponId: couponId,
                showErrorMsg: false
            };
            await this._orderService.order.checkCouponHasQualifyingItemsAsync(this._loyaltyProvider, params);
        } else
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("LOCATION_OFFER_UNAVAILABLE"),
                true
            );

        this._context.setProperty("couponId", undefined);
    }

    private _showPlaceholders(): void {
        Util.hideElement("div_menu_main_section");
        Util.showElement("div_menu_placeholder");
    }

    private _hidePlaceholders(): void {
        Util.hideElement("div_menu_placeholder");
        Util.showElement("div_menu_main_section");
    }

    private readonly _renderPageAsync = async (): Promise<void> => {
        // Build and Select Menu tab
        const mealDealCategories = this._createMealDealsArrayObject();
        const tabData = this._buildMenuBar(mealDealCategories);

        const categoryItems = this._createCategoriesAndItemsObject(mealDealCategories);
        this._buildItemCategoriesSelector(categoryItems);

        const mainItemsDiv = document.getElementById("div_menu_items_parent");
        if (mainItemsDiv) {
            mainItemsDiv.innerHTML = "";
            for (const tab of tabData.tabs) {
                const section = this._buildItemsSection(tab.id);
                if (section)
                    mainItemsDiv.appendChild(section);
            }
        }

        this._hidePlaceholders();

        // Show Items
        this._showItemsOfCategory(this._item.categoryId);

        document.getElementById("btn_menu_menu_tab_-1")?.click();

        // Use Url Anchor Id if exists
        const anchorCategoryId = this._context.getProperty("anchorCategoryId");
        if (anchorCategoryId) {
            this._item.categoryId = anchorCategoryId;
            //2DO: this._context.setProperty("anchorCategoryId", undefined);
        }

        // If default category is a menu category
        const categoryIds = tabData.tabs.map(x => x.id);
        if (!categoryIds.includes(this._item.categoryId)) {
            document.getElementById(`li_menu_items_category_${this._item.categoryId}`)?.click();
        }

        this._syncCategoriesOnScroll();
        window.dispatchEvent(new Event("resize"));

        await this._orderService.order.setSpecialCodeAsync(this._loyaltyProvider);
        await this._orderService.order.setBoxTopperAsync(this._loyaltyProvider);
    }

    private readonly _setEventListeners = (): void => {
        const viewAllButtons = Array.from(document.getElementsByName("btn_menu_items_item_category_viewall"));
        for (const button of viewAllButtons) {
            const categoryId = Number(button.parentElement?.dataset.cid);
            let subCategoryId: number | undefined = Number(button.parentElement?.dataset.id);
            if (subCategoryId == categoryId)
                subCategoryId = undefined;
            button.onclick = () => {
                this._viewAllOnClick(categoryId, subCategoryId);
            };
        }

        document.body.addEventListener("scroll", Util.Debounce(() => {
            const div = document.getElementById("div_menu_categories_bar");
            if (!div) return;

            // Get the scroll position, checking both documentElement and body
            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;

            // Add 'scrolled' class if scrolled down by 10 pixels or more
            if (scrollTop > 10) {
                div.classList.add("scrolled");
            } else {
                div.classList.remove("scrolled");
            }
        }, this.DEBOUNCE_INTERVAL));
    }

    private readonly _renderPlaceholders = (): void => {
        const div = document.getElementById("div_menu_placeholder");
        if (!div)
            return;

        let htmlTabs = "";
        for (let i = 0; i < 7; i++) {
            htmlTabs += ` 
                <div class="placeholder placeholder-menu-tabs">
                    <div class="placeholder-option"></div>
                </div>`;
        }

        let htmlParentItems = "";
        for (let i = 0; i < 2; i++) {
            let htmlItems = "";
            for (let j = 0; j < 8; j++) {
                htmlItems +=
                    `<div class="placeholder placeholder-menu-card">
                        <div class="placeholder-option"></div>
                    </div>`;
            }

            htmlParentItems += ` 
                <div class="placeholder placeholder-menu-category-header">
                    <div class="placeholder-option"></div>
                </div>
                <div class="menu-item-card-parent">${htmlItems}</div>`;
        }

        const html = `
            <div class="categories-bar placeholder-categories-bar">${htmlTabs}</div>

            <div>
                <div class="menu-parent-wrapper">${htmlParentItems}</div>
            </div>`;

        div.innerHTML = html;
    }

    private readonly _buildMenuBar = (mealDealCategories: IMealDealData[]): ITabBarData => {
        const tabsDiv = document.getElementById("div_menu_menu_tabs");
        if (!tabsDiv)
            return { buttonId: "", tabs: [] };

        tabsDiv.innerHTML = "";

        const isMealDealAsTab = this._showMealDealAsTab();
        const isUserLoggedIn = this._profileService.user.isLoggedIn();
        const hasMenuTabs = (isMealDealAsTab || !isMealDealAsTab && isUserLoggedIn);

        const buttonId = "btn_menu_menu_tab";
        const menuTabId = -1;
        const tabData: ITabBarData = {
            buttonId: buttonId,
            tabs: [{
                id: menuTabId, text: this._localizationService.names("Menu"), ltag: "Menu", function: () => {
                    const btn = document.getElementById(`${buttonId}_${menuTabId}`) ?? undefined;
                    const humanClickedElement = btn?.dataset.humanClick === 'true' ? btn : undefined;
                    this._showItemsOfCategory(-1, humanClickedElement);
                }
            }]
        };

        if (isMealDealAsTab) {
            for (const category of mealDealCategories) {
                if (category.coupons.length == 0)
                    continue;

                const name = this._localizationService.GetName(category.names);
                const buttonData = {
                    id: category.id, text: name, ltagj: Util.toLtagj(category.names), function: () => {
                        const btn = document.getElementById(`${buttonId}_${category.id}`) ?? undefined;
                        const humanClickedElement = btn?.dataset.humanClick === 'true' ? btn : undefined;
                        this._showItemsOfCategory(category.id, humanClickedElement)
                    }
                };
                tabData.tabs.push(buttonData);
            }
        }

        // Recent Orders
        const recentlyOrderedId = -2;
        const buttonData = {
            id: recentlyOrderedId, text: this._localizationService.names("RecentlyOrdered"), ltag: "RecentlyOrdered", function: () => {
                const btn = document.getElementById(`${buttonId}_${recentlyOrderedId}`) ?? undefined;
                const humanClickedElement = btn?.dataset.humanClick === 'true' ? btn : undefined;
                this._showItemsOfCategory(-2, humanClickedElement)
            }
        };
        tabData.tabs.push(buttonData);
        
        const tabBar = new TabBar(tabData, this._localizationService);
        tabsDiv.appendChild(tabBar.element);
        tabsDiv.classList.toggle("hidden", !hasMenuTabs);
        this._removeRecentOrderTab(isUserLoggedIn);

        return tabData;
    }

    private readonly _categoryButtonOnClick = (categoryId: number, isInit: boolean = false, clickedElement?: HTMLElement): boolean => {
        this._clearSelectedCategories();

        const anchors = Array.from(document.querySelectorAll(`[name="menu_items_item_category_${categoryId}"]`)) as HTMLElement[];
        if (anchors.length > 0 && !isInit) {
            const categoriesBar = document.getElementById("div_menu_categories_bar");
            const offset = anchors[0].offsetTop - (categoriesBar?.getBoundingClientRect().bottom || 0) - 20;

            document.body.scrollTop = document.documentElement.scrollTop;

            requestAnimationFrame(() => {
                document.body.scrollTo({ top: offset, behavior: 'smooth' });
            });
        }

        this._categoryButtonScrollIntoView(categoryId);

        if (this._dataLayer && clickedElement) {
            const breadCrumb = Util.getMenuBreadcrumb(clickedElement, "LI");
            this._dataLayer.menu_click(breadCrumb.join(' > '), MenuLocationType.HEADER);
        }

        return true;
    }

    private readonly _categoryButtonScrollIntoView = (categoryId: number): void => {
        const container = document.getElementById("ul_menu_items_category");
        const button = document.getElementById(`li_menu_items_category_${categoryId}`);
        if (!button || !container)
            return;

        if (button.classList.contains("active"))
            return;

        let offset = button.offsetLeft - 100;

        if (offset < 0) offset = 0;

        container.scroll({ left: offset, behavior: "smooth" });
        button.classList.add("active");
    }

    private readonly _clearSelectedCategories = (): void => {
        const buttons = Array.from(document.querySelectorAll<HTMLElement>("#ul_menu_items_category>li"));
        for (const button of buttons) {
            button.classList?.remove("active");
        }
    }

    private readonly _showItemsOfCategory = (categoryId: number, clickedElement?: HTMLElement): void => {
        const divs = Array.from(document.getElementsByName("div_menu_items"));
        for (const div of divs) {
            Util.hideElement(div);
        }

        const mealDealDiv = document.getElementById(`div_menu_items_${categoryId}`);
        if (mealDealDiv) {
            Util.hideElement(`div_menu_categories_bar`);
            Util.showElement(`div_menu_items_${categoryId}`);
            document.body.removeEventListener("scroll", Util.Debounce(this._syncCategoriesOnScroll, 10));
        } else if (categoryId == -2) {
            Util.hideElement(`div_menu_categories_bar`);
            Util.showElement(`div_menu_items_recently_ordered`);
            document.body.removeEventListener("scroll", Util.Debounce(this._syncCategoriesOnScroll, 10));
        } else {
            Util.showElement(`div_menu_categories_bar`);
            Util.showElement(`div_menu_items_main`);
            document.body.addEventListener("scroll", Util.Debounce(this._syncCategoriesOnScroll, 10));
        }
        document.body.scrollTop = 0;

        if (this._dataLayer && clickedElement) {
            const breadCrumb = Util.getMenuBreadcrumb(clickedElement, "BUTTON");
            this._dataLayer.menu_click(breadCrumb.join(' > '), MenuLocationType.HEADER);
        }
    }

    private _buildItemsSection(categoryId: number): HTMLElement | null {
        switch (categoryId) {
            case -2:
                return this._buildRecentlyOrderedItemsSection();
            case -1:
                return this._buildMainItemsSection();
            default:
                return this._buildHotDealsItemsSection(categoryId);
        }
    }

    /**
     * Generates an array of menu item categories and their respective items and subcategories.
     * 
     * @returns {IMenuItemsCategory[]} An array of menu item categories.
     */
    private _createCategoriesAndItemsObject(mealDealCategories: IMealDealData[]): IMenuItemsCategory[] {
        // 2DO: Call Center logic?
        // if ((this._localAolo.data.Settings.ISCC && !this._categoryHasItems(category.ItemWebCategoryId, true)) || (!this._categoryHasItems(category.ItemWebCategoryId) && !category.IsMealDeal))
        //     return;

        const categories = (this._showMealDealAsTab() ? this._data.getProperty("Categories")?.filter(x => !x.IsMealDeal) : this._data.getProperty("Categories")) ?? [];
        const menuCategories: IMenuItemsCategory[] = [];

        for (const category of categories) {
            if (category.Default)
                this._item.categoryId = category.ItemWebCategoryId;

            const categoryItem: IMenuItemsCategory = {
                id: category.ItemWebCategoryId,
                names: category.Names,
                isMealDeal: category.IsMealDeal,
                items: [],
                subcategories: [],
                coupons: []
            };

            if (category.IsMealDeal) {
                const mealDealCategory = mealDealCategories.find(x => x.id === category.ItemWebCategoryId);
                categoryItem.coupons = mealDealCategory?.coupons ?? [];
            } else {
                const categoryItems = this._data.getProperty("Items")?.filter(x => x.WebCategoryId === category.ItemWebCategoryId) ?? [];
                // 2DO: Call Center Logic?
                // const storeItems = isCallCenter ? Util.getCallCenterStore(this._localAolo.Order.StoreID, this._localAolo.data.Stores)?.Items ?? "" : "";

                this._processCategoryItems(categoryItems, categoryItem);
            }

            menuCategories.push(categoryItem);
        }

        return menuCategories;
    }

    /**
     * Processes items within a category, assigning them to items or subcategories.
     * 
     * @param {any[]} categoryItems - The items in the category.
     * @param {IMenuItemsCategory} categoryItem - The category object to populate.
     */
    private _processCategoryItems(categoryItems: IDataItem[], categoryItem: IMenuItemsCategory): void {
        let lastSubCategoryId = 0;

        for (const item of categoryItems) {
            // 2DO: Call Center
            //if (this._data.getProperty("Settings")?.ISCC && storeItems !== "") {
            //    const itemId = `*${mItem.ItemId}*`;
            //    if (storeItems.indexOf(itemId) === -1)
            //        continue;
            //}

            // Skip items with no valid sizes or QR restrictions
            if (!OnlineOrderingUtil._isItemValid(item, this._item.orderTypeId, !!this._context.getProperty("qrCode")))
                return;

            if (this._isNewSubCategory(item.SubCategoryId, lastSubCategoryId)) {
                lastSubCategoryId = item.SubCategoryId;
                this._addSubCategory(item.SubCategoryId, categoryItem);
            }

            if (item.SubCategoryId === 0) {
                categoryItem.items.push(item);
            } else {
                const subcategory = categoryItem.subcategories.find(subCat => subCat.id === item.SubCategoryId);
                subcategory?.items.push(item);
            }
        }
    }

    /**
     * Determines if a new subcategory needs to be created.
     * 
     * @param {number} subCategoryId - The current subcategory ID.
     * @param {number} lastSubCategoryId - The ID of the last processed subcategory.
     * @returns {boolean} Whether a new subcategory is needed.
     */
    private _isNewSubCategory(subCategoryId: number, lastSubCategoryId: number): boolean {
        return subCategoryId !== 0 && subCategoryId !== lastSubCategoryId;
    }

    /**
     * Adds a subcategory to the category item.
     * 
     * @param {number} subCategoryId - The ID of the subcategory.
     * @param {IMenuItemsCategory} categoryItem - The category object to populate.
     */
    private _addSubCategory(subCategoryId: number, categoryItem: IMenuItemsCategory): void {
        const subcategory = this._data.getProperty("SubCategories")?.find(subCat => subCat.ItemSubCategoryId === subCategoryId);

        if (subcategory) {
            categoryItem.subcategories.push({
                id: subcategory.ItemSubCategoryId,
                names: subcategory.Names,
                items: []
            });
        }
    }

    private readonly _createMealDealsArrayObject = (): IMealDealData[] => {
        const array: IMealDealData[] = [];
        const mealDealCategories = this._data.getProperty("Categories")?.filter(x => x.IsMealDeal) ?? [];

        for (const category of mealDealCategories) {
            if (category.Default)
                this._item.categoryId = category.ItemWebCategoryId;

            const mealDealCategory: IMealDealData = {
                id: category.ItemWebCategoryId,
                names: category.Names,
                default: category.Default,
                coupons: this._getMealDealCoupons(category.ItemWebCategoryId),
            };

            array.push(mealDealCategory);
        }
        return array;
    }

    private readonly _buildMainItemsSection = (): HTMLElement => {
        const mealDealCategories = this._createMealDealsArrayObject();
        const categoryItems = this._createCategoriesAndItemsObject(mealDealCategories);
        const itemsSection = this._buildItemsSections(categoryItems);
        return itemsSection;
    }

    private _checkIfCategoryHasNoItemsToShow(category: IMenuItemsCategory): boolean {
        return (category.items.length === 0 && !category.subcategories.some(x => x.items.length > 0))
            && (!category.isMealDeal || (category.isMealDeal && category.coupons.length === 0));
    }

    private readonly _buildItemCategoriesSelector = (categoryItems: IMenuItemsCategory[]): void => {
        const categoriesBarDiv = document.getElementById("div_menu_categories_bar");
        if (!categoriesBarDiv)
            return;

        const isMealDealAsTab = this._showMealDealAsTab();
        const isUserLoggedIn = this._profileService.user.isLoggedIn();
        const hasMenuTabs = (isMealDealAsTab || !isMealDealAsTab && isUserLoggedIn);
        categoriesBarDiv.classList.toggle("tab-menu-present", hasMenuTabs);
        categoriesBarDiv.innerHTML = "";

        const buttonId = "menu_items_category";
        const data: IPillBarData = {
            buttonId: buttonId,
            buttons: []
        };

        for (const category of categoryItems) {
            if (this._checkIfCategoryHasNoItemsToShow(category))
                continue;

            const name = this._localizationService.GetName(category.names);
            data.buttons.push(
                {
                    id: category.id.toString(),
                    text: name,
                    selected: category.id === this._item.categoryId,
                    ltagj: category.names,
                    function: () => {
                        this._context.setProperty("upsellSelected", false);
                        const btn = document.getElementById(`li_${buttonId}_${category.id.toString()}`) ?? undefined;
                        const humanClickedElement = btn && btn.dataset.humanClick === 'true' ? btn : undefined;
                        return this._categoryButtonOnClick(category.id, false, humanClickedElement);
                    }
                }
            );
        }

        const pillBar = new PillBar(data, this._localizationService);
        categoriesBarDiv.appendChild(pillBar.element);
    }

    /**
     * Builds a section of menu items categorized by categories and subcategories.
     *
     * @param {IMenuItemsCategory[]} categoryItems - The list of menu item categories.
     * @returns {HTMLElement} The parent HTML element containing the built menu sections.
     */
    private readonly _buildItemsSections = (categoryItems: IMenuItemsCategory[]): HTMLElement => {
        const parentDiv = this._createParentDiv();

        for (const category of categoryItems) {
            if (this._checkIfCategoryHasNoItemsToShow(category))
                continue;

            if (category.isMealDeal) {
                const categoryHeader = this._createCategoryHeader(category);
                parentDiv.appendChild(categoryHeader);

                const categoryItemsSection = this._createMealDealSection(category.coupons);
                parentDiv.appendChild(categoryItemsSection);
            } else {
                if (category.items.length !== 0) {
                    const categoryHeader = this._createCategoryHeader(category);
                    parentDiv.appendChild(categoryHeader);

                    const categoryItemsSection = this._createItemsSection(category.items);
                    parentDiv.appendChild(categoryItemsSection);
                }

                for (const subcategory of category.subcategories) {
                    this._appendSubcategorySection(parentDiv, category, subcategory);
                }
            }
        }

        return parentDiv;
    }

    /**
     * Creates the parent div container for the menu items.
     *
     * @returns {HTMLElement} The parent div element.
     */
    private readonly _createParentDiv = (): HTMLElement => {
        const parentDiv = document.createElement("div");
        parentDiv.id = "div_menu_items_main";
        parentDiv.setAttribute("name", "div_menu_items");
        parentDiv.classList.add("menu-parent-wrapper");
        return parentDiv;
    }

    /**
     * Creates and appends a subcategory section, including its items, to the parent container.
     *
     * @param {HTMLElement} parentDiv - The parent div to append sections to.
     * @param {IMenuItemsCategory} category - The parent category data.
     * @param {IMenuItemsCategory} subcategory - The subcategory data.
     */
    private _appendSubcategorySection(parentDiv: HTMLElement, category: IMenuItemsCategory, subcategory: IMenuItemsCategoriesSubcategory): void {
        const subcategoryHeader = this._createCategoryHeader(category, subcategory);
        const subcategoryItemsSection = this._createItemsSection(subcategory.items);

        parentDiv.appendChild(subcategoryHeader);
        parentDiv.appendChild(subcategoryItemsSection);
    }

    /**
     * Creates a section for items within a category or subcategory.
     *
     * @param {IDataItem[]} items - The items to be included in the section.
     * @returns {HTMLElement} The HTML element containing item cards.
     */
    private readonly _createItemsSection = (items: IDataItem[]): HTMLElement => {
        const itemsSection = document.createElement("div");
        itemsSection.classList.add("menu-item-card-parent");

        let itemIndex = 0;
        for (const item of items) {
            const itemCard = new MenuItemCard(this._localizationService, item, () => { this._menuItemCardOnClick(item, 1, itemIndex); });
            itemsSection.appendChild(itemCard.element);
            itemIndex++;
        }

        const arrowScroller = new ArrowScroller(itemsSection, { localizationService: this._localizationService });

        return arrowScroller.element;
    }

    private readonly _menuItemCardOnClick = (mItem: IDataItem, quantity: number, index: number): void => {
        const orderTypeId = this._orderService.order.getProperty("OrderTypeID");
        const defaultSizeId = this._data.getProperty("Settings")?.DSZID;

        if (!(orderTypeId && defaultSizeId)) return;

        const defaultSize = OnlineOrderingUtil.GetItemDefaultSize(mItem, orderTypeId, null, defaultSizeId);

        if (this._dataLayer) {
            const orderItem = this._orderService.order._createOrderItem(mItem, defaultSize.SizeId, 0, quantity, []);
            this._dataLayer.select_item(this._orderService.order, orderItem, index, Pages.MENU.replace('/', ''));
        }

        OnlineOrderingUtil.checkAgeAndCustomizeItem(this._context, this._localizationService, mItem, () => {
            const props: ICustomizeItemProps = {
                sizeId: defaultSize.SizeId,
                quantity: quantity,
                mealDeal: null,
                recentOrder: false,
                mItem: mItem,
                itemsToEdit: [],
                elementIndex: index
            };

            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.CUSTOMIZE_ITEM, props);
        });
    }

    /**
     * Creates a section for hot deal within a category.
     *
     * @param {IDataCoupon[]} coupons - The items to be included in the section.
     * @returns {HTMLElement} The HTML element containing item cards.
     */
    private _createMealDealSection(coupons: IDataCoupon[]): HTMLElement {
        const section = document.createElement("div");
        section.classList.add("menu-item-card-parent");

        let itemIndex = 0;
        for (const coupon of coupons) {
            const itemObj: any = {
                ItemId: coupon.CouponId,
                ImageURL: coupon.ImageURL ?? "",
                Names: coupon.Names,
                Descriptions: coupon.Descriptions
            };
            const itemCard = new MenuItemCard(this._localizationService, itemObj, () => { this._menuItemCouponCardOnClick(coupon, itemIndex); });
            section.appendChild(itemCard.element);
            itemIndex++;
        }

        const arrowScroller = new ArrowScroller(section, { localizationService: this._localizationService });

        return arrowScroller.element;
    }

    private readonly _menuItemCouponCardOnClick = async (coupon: IDataCoupon, index: number): Promise<void> => {
        if (!this._orderService.order.isCouponAllowedInFundraiser())
            return;

        const params: ICheckCouponHasQualifyingItemsParams = {
            couponId: coupon.CouponId
        };
        await this._orderService.order.checkCouponHasQualifyingItemsAsync(this._loyaltyProvider, params);

        if (this._dataLayer)
            this._dataLayer.select_promotion(coupon.CouponId, coupon.Names, DataLayerPromotionLocation.MEAL_DEAL, index);
    }

    private readonly _createCategoryHeader = (category: IMenuItemsCategory, subcategory?: IMenuItemsCategoriesSubcategory): HTMLElement => {
        let html = "";
        if (subcategory) {
            const name = this._localizationService.GetName(subcategory.names);
            html = `
                <div id="div_menu_items_item_category_${category.id}_${subcategory.id}" name="menu_items_item_category_${category.id}" class="category-header" data-id="${subcategory.id}" data-cid="${category.id}">
                    <div ltagj="${Util.toLtagj(subcategory.names)}">${name}</div>
                    <button id="btn_menu_items_item_category_viewall_${category.id}_${subcategory.id}" name="btn_menu_items_item_category_viewall" class="button-link">${this._localizationService.names("ViewAll")}</button>
                </div>`;
        } else {
            const name = this._localizationService.GetName(category.names);
            html = `
                <div id="div_menu_items_item_category_${category.id}" name="menu_items_item_category_${category.id}" class="category-header" data-id="${category.id}" data-cid="${category.id}">
                    <div ltagj="${Util.toLtagj(category.names)}">${name}</div>
                    <button id="btn_menu_items_item_category_viewall_${category.id}" name="btn_menu_items_item_category_viewall" class="button-link">${this._localizationService.names("ViewAll")}</button>
                </div>`;
        }

        return Util.createHtmlElementFromTemplate(html);
    }

    private readonly _syncCategoriesOnScroll = (): void => {
        if (this._item.isScrolling)
            return;

        const categories = Array.from(document.querySelectorAll(".category-header")) as HTMLDivElement[];
        const windowHeight = window.innerHeight;

        let activeCategoryId = 0;
        for (const category of categories) {
            if (this._getElementTop(category, "") > windowHeight * 0.50)
                break;

            activeCategoryId = Number(category.dataset.cid);
        }

        if (activeCategoryId === 0)
            return;

        this._clearSelectedCategories();
        this._categoryButtonScrollIntoView(activeCategoryId);
    }

    private _getElementTop(el: HTMLElement | null, parentID: string): number {
        if (!el)
            return 0;

        let top = 0;
        while (el) {
            const isTopElement = (el.id === parentID || el.tagName === "BODY");
            const yScroll = isTopElement ? el.scrollTop || document.documentElement.scrollTop : el.scrollTop;
            top += el.offsetTop - yScroll + el.clientTop;
            el = el.offsetParent as HTMLElement | null;
        }
        return top;
    }

    private readonly _buildHotDealsItemsSection = (categoryId: number): HTMLElement | null => {
        const coupons = this._getMealDealCoupons(categoryId);

        if (coupons.length == 0)
            return null;

        const parentDiv = document.createElement("div");
        parentDiv.id = `div_menu_items_${categoryId}`;
        parentDiv.setAttribute("name", "div_menu_items");
        parentDiv.classList.add("meal-deal-items-parent");

        let index = 0;
        for (const coupon of coupons) {
            const card = new MealDealItemCard(this._data, this._orderService.order, this._localizationService, this._loyaltyProvider, this._dataLayer, coupon, index);
            parentDiv.appendChild(card.element);
            index++;
        }

        return parentDiv;
    }

    private readonly _getMealDealCoupons = (categoryId: number): IDataCoupon[] => {
        const couponsData = this._data.getProperty("Coupons") || [];
        const orderTypeId = this._orderService.order.getProperty("OrderTypeID");
        if (couponsData.length === 0 || orderTypeId == null)
            return [];

        return couponsData.filter(x => !x.IsHiddenOnline
            && x.WebCategoryId === categoryId
            && this._orderService.order.checkCouponOrderTypeSubType(x, orderTypeId)
            && this._orderService.order.checkCouponSchedule(x)
            && this._orderService.order.verifyCouponDate(x)
            && (!x.IsAppOnly || (x.IsAppOnly && Util.isAppView()))
            && (!x.IsProfileRequired || (x.IsProfileRequired && this._profileService.user.isLoggedIn())));
    }

    private readonly _buildRecentlyOrderedItemsSection = (): HTMLElement => {
        const div = document.createElement("div");
        div.id = this._idRecentOrders;
        div.setAttribute("name", "div_menu_items");
        this._recentOrders.buildRecentlyOrderedItemsSection(div.id);
        return div;
    }

    private readonly _setRecentOrderedButtonState = (isLoggedIn: boolean): void => {
        // Hide tab bar when set to not show meal deal and is not logged in
        const isMealDealAsTab = this._showMealDealAsTab();
        const removeTabBar = !isMealDealAsTab && !isLoggedIn;
        document.getElementById("div_menu_menu_tabs")?.classList.toggle("hidden", removeTabBar);
        document.getElementById("div_menu_categories_bar")?.classList.toggle("tab-menu-present", !removeTabBar);

        if (removeTabBar)
            document.getElementById("btn_menu_menu_tab_-1")?.click();

        this._recentOrders.buildRecentlyOrderedItemsSection("div_menu_items_recently_ordered");

        this._removeRecentOrderTab(isLoggedIn);
    }

    private _removeRecentOrderTab(isLoggedIn: boolean): void {
        const button = document.querySelector<HTMLElement>('button[ltag="RecentlyOrdered"]');
        if (button) {
            isLoggedIn ? Util.showElement(button) : Util.hideElement(button);
        }
    }

    private _showMealDealAsTab(): boolean {
        return this._data.getProperty("Settings").OOS.mealDealLocation === MealDealLocationUiOption.MEAL_DEAL_AS_TAB;
    }

    private readonly _viewAllOnClick = (categoryId: number, subcategoryId?: number): void => {
        const mealDealCoupons = this._getMealDealCoupons(categoryId);
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU_VIEW_ALL, false, categoryId, subcategoryId, mealDealCoupons);
    }
}